import React, { useState } from 'react';
import { AlertTriangle, FileUp, Loader2, Upload } from 'lucide-react';
import { Heading } from '../layout';
import { useUIColors } from '../../hooks/useTheme';
import { apiClient, API_BASE_URL, suspendUnauthorizedReload } from '../../services/apiClient';
import AdministrationBackLink from '../system/AdministrationBackLink';
import {
  DUMP_FORMAT,
  buildRowChunks,
  isTransientError,
  orderRowsForSelfFk,
  parseDumpFile,
  summarizeDump,
  withRetries,
} from '../system/datos/dataTransferCatalog';

/** Cada tramo debe responder holgadamente dentro del límite de espera de CloudFront (60 s). */
const IMPORT_CHUNK_MAX_ROWS = 1000;
const IMPORT_CHUNK_MAX_BYTES = 1000000;
const IMPORT_POLL_MS = 2000;
const USER_ACCESS_TABLES = ['users', 'user_roles', 'roles', 'role_permissions', 'permissions'];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const describeApplyProgress = (progress = {}) => {
  const rows =
    progress.rows_total > 0
      ? ` · ${Number(progress.rows_done || 0).toLocaleString('es-CL')}/${Number(progress.rows_total).toLocaleString('es-CL')} filas`
      : '';
  const tableLabel = `${progress.table} (tabla ${progress.table_index}/${progress.table_count})`;
  switch (progress.phase) {
    case 'deleting':
      return `Aplicando: quitando filas que no vienen en el archivo · ${tableLabel}…`;
    case 'upserting':
      return `Aplicando: agregando y actualizando · ${tableLabel}${rows}…`;
    case 'inserting':
      return `Aplicando: agregando filas nuevas · ${tableLabel}${rows}…`;
    case 'sequences':
      return `Aplicando: ajustando secuencias de IDs${rows}…`;
    case 'committing':
      return 'Aplicando: confirmando la transacción…';
    default:
      return 'Aplicando: iniciando…';
  }
};

const IMPORT_MODES = {
  replace: {
    label: 'Reemplazar tablas seleccionadas',
    button: 'Importar y reemplazar',
    confirm:
      'Confirmo que las tablas seleccionadas deben quedar igual que en el archivo: se agregarán y actualizarán sus filas, y se eliminarán las que no vienen en él.',
    warning:
      'Las tablas seleccionadas quedan igual que en el archivo: primero se agregan y actualizan sus filas, y después se eliminan las que no vienen en él. Solo se conservan filas extra si las sigue usando una tabla excluida (si no, no se podría tocar esa tabla). Las tablas excluidas no se modifican. Todo se aplica en una sola transacción: si algo falla, la base queda igual que antes.',
    description:
      'Sube el archivo por tramos de hasta 1.000 filas y luego sincroniza tabla por tabla en una sola transacción. No modifica el esquema ni alembic_version.',
  },
  append: {
    label: 'Solo agregar faltantes',
    button: 'Importar faltantes',
    confirm:
      'Confirmo que quiero agregar solo las filas del archivo que aún no existen en la base (por clave primaria / unicidad).',
    warning:
      'Modo seguro: no se borran datos. Se insertan solo filas nuevas; si la clave primaria o un unique ya existe, esa fila se omite.',
    description:
      'Conserva los datos actuales e inserta únicamente entradas que no estén ya en la base (conflicto de PK o unique → se omite). Sube el archivo por tramos y lo aplica en una sola transacción.',
  },
};

const SystemDataImportWorkspace = ({ isDarkMode = false, showAdminBack = false }) => {
  const ui = useUIColors();
  void isDarkMode;

  const [fileName, setFileName] = useState('');
  const [dump, setDump] = useState(null);
  const [importMode, setImportMode] = useState('append');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState('');
  const [excludedTables, setExcludedTables] = useState([]);

  const modeMeta = IMPORT_MODES[importMode] || IMPORT_MODES.append;
  const isReplace = importMode === 'replace';
  const excludedSet = new Set(excludedTables);
  const toggleExcluded = (name) => {
    setResult(null);
    setExcludedTables((prev) => (prev.includes(name) ? prev.filter((t) => t !== name) : [...prev, name]));
  };

  const cardStyle = {
    border: `1px solid ${ui.border}`,
    borderRadius: '12px',
    background: ui.panel,
    padding: '14px 16px',
  };

  const stats = dump ? summarizeDump(dump) : null;

  const secondaryButtonStyle = (disabled) => ({
    height: '28px',
    borderRadius: '8px',
    border: `1px solid ${ui.inputBorder}`,
    background: ui.inputBg,
    color: ui.text,
    fontWeight: 600,
    fontSize: '12px',
    padding: '0 10px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
  });

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError('');
    setResult(null);
    setConfirmed(false);
    setExcludedTables([]);
    setFileName(file.name);
    try {
      const parsed = await parseDumpFile(file);
      setDump(parsed);
    } catch (err) {
      setDump(null);
      setError(err.message || 'No se pudo leer el archivo.');
    }
  };

  const handleImport = async () => {
    if (!dump) {
      setError('Carga un archivo de dump JSON antes de importar.');
      return;
    }
    if (!confirmed) {
      setError('Debes confirmar la operación antes de importar.');
      return;
    }

    const fileTables = Object.keys(dump.tables || {});
    const tableNames = fileTables.filter((name) => !excludedSet.has(name));
    const excludedNames = fileTables.filter((name) => excludedSet.has(name));
    if (!tableNames.length) {
      setError('Todas las tablas están excluidas: selecciona al menos una para importar.');
      return;
    }
    const notArrays = tableNames.filter((name) => !Array.isArray(dump.tables[name]));
    if (notArrays.length) {
      setError(`El archivo es inválido: estas tablas no contienen una lista de filas: ${notArrays.join(', ')}.`);
      return;
    }

    setBusy(true);
    setError('');
    setResult(null);
    setProgress('');

    let importToken = '';
    const post = (path, body) =>
      apiClient(`${API_BASE_URL}/api/system/data/import/${path}`, {
        method: 'POST',
        timeoutMs: 600000,
        body: JSON.stringify(importToken ? { ...body, import_token: importToken } : body),
      });

    let retries = 0;
    let currentStep = 'paso de preparación';
    // prepare | upload | apply-request | apply-running
    let phase = 'prepare';
    const runStep = (label, fn, options = {}) =>
      withRetries(fn, {
        ...options,
        onRetry: (attempt, err, delayMs) => {
          retries += 1;
          setProgress(
            `${label}: el intento ${attempt} falló (${err.message}). Reintentando en ${Math.round(delayMs / 1000)} s…`
          );
        },
      });

    suspendUnauthorizedReload(true);
    try {
      setProgress('Preparando importación…');
      const prep = await runStep('Preparación', () =>
        post('prepare', {
          format: dump.format,
          mode: importMode,
          confirm: true,
          tables: tableNames,
          excluded_tables: excludedNames,
        })
      );
      importToken = prep.import_token || '';
      phase = 'upload';

      const plan = (prep.insert_order || []).map((table) => ({
        table,
        chunks: buildRowChunks(orderRowsForSelfFk(dump.tables[table], prep.self_fk_columns?.[table] || []), {
          maxRows: IMPORT_CHUNK_MAX_ROWS,
          maxBytes: IMPORT_CHUNK_MAX_BYTES,
        }),
      }));
      const totalChunks = plan.reduce((acc, item) => acc + item.chunks.length, 0);

      let chunkNumber = 0;
      for (const { table, chunks } of plan) {
        for (const chunk of chunks) {
          chunkNumber += 1;
          const firstRow = chunk.offset + 1;
          const lastRow = chunk.offset + chunk.rows.length;
          currentStep =
            `tramo ${chunkNumber}/${totalChunks}: ${table} ` +
            `(filas ${firstRow.toLocaleString('es-CL')}–${lastRow.toLocaleString('es-CL')})`;
          setProgress(`Subiendo ${currentStep}…`);
          await runStep(`Tramo ${chunkNumber}/${totalChunks}`, () =>
            post('chunk', { table, row_offset: chunk.offset, rows: chunk.rows })
          );
        }
      }

      phase = 'apply-request';
      currentStep = 'paso de aplicación en la base';
      setProgress('Aplicando en la base de datos…');
      const expectedRows = Object.fromEntries(plan.map(({ table }) => [table, dump.tables[table].length]));
      await runStep('Aplicación', () => post('apply', { expected_rows: expectedRows }));

      phase = 'apply-running';
      let job;
      for (;;) {
        await sleep(IMPORT_POLL_MS);
        job = await runStep('Consulta de avance', () => post('status', {}), {
          attempts: 6,
          delaysMs: [2000, 5000, 10000, 15000, 20000],
        });
        if (job.status === 'done' || job.status === 'failed') break;
        if (job.stale) {
          const staleError = new Error(
            `El servidor dejó de reportar avance hace ${Math.round((job.idle_seconds || 0) / 60)} min ` +
              '(posible reinicio del backend durante la importación).'
          );
          staleError.jobStale = true;
          throw staleError;
        }
        setProgress(describeApplyProgress(job.progress));
      }

      if (job.status === 'failed') {
        const failure = new Error(job.error?.message || 'La importación falló en el servidor sin detalle.');
        failure.jobFailed = true;
        throw failure;
      }

      setResult({ ...job.result, chunk_count: totalChunks, retries });
      setConfirmed(false);
    } catch (err) {
      const reason = err.message || 'Error desconocido al importar.';
      let state;
      if (err.jobFailed) {
        state = 'La importación se revirtió completa: la base quedó exactamente como estaba antes de importar.';
      } else if (err.jobStale) {
        state =
          'Si el proceso se interrumpió, PostgreSQL revierte la transacción y la base queda como estaba. ' +
          'Revisa el resumen de tablas en Exportación antes de reintentar.';
      } else if (phase === 'apply-running') {
        state =
          'No se pudo confirmar el resultado: la importación puede seguir aplicándose en el servidor. ' +
          'Espera unos minutos y revisa el resumen de tablas en Exportación antes de reintentar.';
      } else if (phase === 'apply-request' && isTransientError(err)) {
        state =
          'No se sabe si el servidor alcanzó a iniciar la aplicación. Espera unos minutos y revisa el ' +
          'resumen de tablas en Exportación antes de reintentar.';
      } else {
        state = 'No se modificó la base de datos; puedes corregir el problema y volver a importar.';
      }
      setError(`Falló el ${currentStep}. ${reason}\n${state}`);
    } finally {
      suspendUnauthorizedReload(false);
      setBusy(false);
      setProgress('');
    }
  };

  return (
    <div style={{ display: 'grid', gap: '14px' }}>
      <div>
        <Heading level={2} isDarkMode={isDarkMode}>
          Importación de Datos
        </Heading>
        {showAdminBack ? <AdministrationBackLink /> : null}
      </div>

      <div style={cardStyle}>
        <p style={{ margin: '0 0 12px', fontSize: '13px', color: ui.muted, lineHeight: 1.5 }}>
          Carga un archivo JSON generado por Exportación (formato <code>{DUMP_FORMAT}</code>).{' '}
          {modeMeta.description}
        </p>

        <div style={{ marginBottom: '12px' }}>
          <div
            style={{
              fontSize: '11px',
              fontWeight: 700,
              color: ui.muted,
              letterSpacing: '0.04em',
              marginBottom: '8px',
            }}
          >
            MODO DE IMPORTACIÓN
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            {Object.entries(IMPORT_MODES).map(([key, meta]) => (
              <label
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  color: ui.text,
                  cursor: 'pointer',
                  fontWeight: importMode === key ? 700 : 500,
                }}
              >
                <input
                  type="radio"
                  name="import-mode"
                  value={key}
                  checked={importMode === key}
                  onChange={() => {
                    setImportMode(key);
                    setConfirmed(false);
                    setResult(null);
                    setError('');
                  }}
                />
                {meta.label}
              </label>
            ))}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            gap: '10px',
            alignItems: 'flex-start',
            padding: '10px 12px',
            borderRadius: '8px',
            background: isReplace ? 'rgba(234, 179, 8, 0.12)' : 'rgba(34, 197, 94, 0.1)',
            border: isReplace
              ? '1px solid rgba(234, 179, 8, 0.35)'
              : '1px solid rgba(34, 197, 94, 0.3)',
            marginBottom: '12px',
          }}
        >
          <AlertTriangle
            size={18}
            color={isReplace ? '#ca8a04' : '#16a34a'}
            style={{ flexShrink: 0, marginTop: 1 }}
          />
          <p style={{ margin: 0, fontSize: '12px', color: ui.text, lineHeight: 1.45 }}>
            {modeMeta.warning}
          </p>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
          <label
            style={{
              height: '34px',
              borderRadius: '8px',
              border: `1px solid ${ui.inputBorder}`,
              background: ui.inputBg,
              color: ui.text,
              fontWeight: 700,
              fontSize: '13px',
              padding: '0 14px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Upload size={16} />
            Elegir archivo JSON
            <input type="file" accept=".json,application/json" onChange={handleFileChange} style={{ display: 'none' }} />
          </label>

          <button
            type="button"
            onClick={handleImport}
            disabled={busy || !dump || !confirmed}
            style={{
              height: '34px',
              borderRadius: '8px',
              border: 'none',
              background: isReplace ? ui.danger || '#dc2626' : ui.interactive,
              color: '#fff',
              fontWeight: 700,
              fontSize: '13px',
              padding: '0 14px',
              cursor: busy || !dump || !confirmed ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              opacity: busy || !dump || !confirmed ? 0.65 : 1,
            }}
          >
            {busy ? <Loader2 size={16} /> : <FileUp size={16} />}
            {busy ? 'Importando…' : modeMeta.button}
          </button>
        </div>

        {fileName ? (
          <p style={{ margin: '10px 0 0', fontSize: '12px', color: ui.text }}>
            Archivo: <strong>{fileName}</strong>
            {stats
              ? ` · ${stats.tableCount} tablas · ${stats.totalRows.toLocaleString('es-CL')} filas`
              : ''}
            {stats?.exportedAt ? ` · exportado ${stats.exportedAt}` : ''}
            {stats?.version ? ` · v${stats.version}` : ''}
          </p>
        ) : null}
        {stats?.missingKnownTables?.length ? (
          <p style={{ margin: '8px 0 0', fontSize: '12px', color: ui.muted, lineHeight: 1.45 }}>
            Este archivo no incluye tablas del esquema actual: {stats.missingKnownTables.join(', ')}. No se
            modificarán.
          </p>
        ) : null}

        {dump ? (
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              marginTop: '12px',
              fontSize: '13px',
              color: ui.text,
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span>{modeMeta.confirm}</span>
          </label>
        ) : null}

        {busy && progress ? (
          <p style={{ margin: '10px 0 0', fontSize: '13px', color: ui.muted, lineHeight: 1.45 }}>{progress}</p>
        ) : null}
        {error ? (
          <p style={{ margin: '10px 0 0', fontSize: '13px', color: ui.danger, lineHeight: 1.45, whiteSpace: 'pre-line' }}>
            {error}
          </p>
        ) : null}
        {result?.ok ? (
          <div style={{ marginTop: '10px', fontSize: '13px', color: ui.success }}>
            Importación completada ({result.mode === 'append' ? 'solo faltantes' : 'reemplazo'}):{' '}
            {result.imported_tables} tablas ·{' '}
            {Number(result.imported_rows || 0).toLocaleString('es-CL')} filas insertadas
            {Number(result.updated_rows || 0) > 0
              ? ` · ${Number(result.updated_rows).toLocaleString('es-CL')} actualizadas`
              : ''}
            {Number(result.deleted_rows || 0) > 0
              ? ` · ${Number(result.deleted_rows).toLocaleString('es-CL')} eliminadas (no venían en el archivo)`
              : ''}
            {Number(result.skipped_rows || 0) > 0
              ? ` · ${Number(result.skipped_rows).toLocaleString('es-CL')} ${
                  result.mode === 'append' ? 'omitidas (ya existían)' : 'sin cambios'
                }`
              : ''}
            {result.chunk_count ? ` · ${result.chunk_count} tramos` : ''}
            {result.duration_seconds != null
              ? ` · aplicada en ${Number(result.duration_seconds).toLocaleString('es-CL')} s`
              : ''}
            .
            {result.retries ? (
              <p style={{ margin: '6px 0 0', color: ui.muted, fontSize: '12px' }}>
                Hubo {result.retries} reintento(s) por cortes de conexión o del proxy; no afectan el resultado.
              </p>
            ) : null}
            {result.skipped_unknown_columns && Object.keys(result.skipped_unknown_columns).length ? (
              <p style={{ margin: '6px 0 0', color: ui.muted, fontSize: '12px' }}>
                Columnas del archivo que no existen en esta base (ignoradas):{' '}
                {Object.entries(result.skipped_unknown_columns)
                  .map(([table, cols]) => `${table} (${cols.join(', ')})`)
                  .join('; ')}
              </p>
            ) : null}
            {result.unknown_tables_ignored?.length ? (
              <p style={{ margin: '6px 0 0', color: ui.muted, fontSize: '12px' }}>
                Tablas ignoradas (no existen en esta base): {result.unknown_tables_ignored.join(', ')}
              </p>
            ) : null}
            {result.excluded_tables?.length ? (
              <p style={{ margin: '6px 0 0', color: ui.muted, fontSize: '12px' }}>
                Tablas excluidas (no se modificaron): {result.excluded_tables.join(', ')}
              </p>
            ) : null}
            {result.kept_by_table && Object.keys(result.kept_by_table).length ? (
              <p style={{ margin: '6px 0 0', color: ui.muted, fontSize: '12px' }}>
                Filas que no venían en el archivo pero se conservaron porque las usa una tabla no importada:{' '}
                {Object.entries(result.kept_by_table)
                  .map(
                    ([table, byChild]) =>
                      `${table} (${Object.entries(byChild)
                        .map(([child, count]) => `${count} usadas por ${child}`)
                        .join(', ')})`
                  )
                  .join('; ')}
              </p>
            ) : null}
            {result.missing_known_tables_in_dump?.length ? (
              <p style={{ margin: '6px 0 0', color: ui.muted, fontSize: '12px' }}>
                Tablas del esquema actual que no venían en el archivo (no se modificaron):{' '}
                {result.missing_known_tables_in_dump.join(', ')}
              </p>
            ) : null}
            {result.remapped_foreign_keys &&
            Object.keys(result.remapped_foreign_keys).length ? (
              <p style={{ margin: '6px 0 0', color: ui.muted, fontSize: '12px' }}>
                Referencias que quedaron vacías porque apuntaban a registros que no existen en una tabla no
                importada:{' '}
                {Object.entries(result.remapped_foreign_keys)
                  .map(([key, count]) => `${key} (${count.toLocaleString('es-CL')})`)
                  .join(', ')}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 10px', fontSize: '14px', color: ui.text }}>Contenido del archivo</h3>
        {stats?.tables?.length ? (
          <div style={{ marginBottom: '10px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: ui.text, marginRight: '4px' }}>
                {stats.tables.length - excludedTables.length} de {stats.tables.length} tablas seleccionadas ·{' '}
                {stats.tables
                  .filter((table) => !excludedSet.has(table.name))
                  .reduce((acc, table) => acc + table.rows, 0)
                  .toLocaleString('es-CL')}{' '}
                filas
              </span>
              <button
                type="button"
                disabled={busy || !excludedTables.length}
                onClick={() => setExcludedTables([])}
                style={secondaryButtonStyle(busy || !excludedTables.length)}
              >
                Incluir todas
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  const present = new Set(stats.tables.map((table) => table.name));
                  setResult(null);
                  setExcludedTables((prev) =>
                    Array.from(new Set([...prev, ...USER_ACCESS_TABLES.filter((name) => present.has(name))]))
                  );
                }}
                style={secondaryButtonStyle(busy)}
              >
                Excluir usuarios y accesos
              </button>
            </div>
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: ui.muted, lineHeight: 1.45 }}>
              Desmarca las tablas que no quieres importar: quedan tal como están en esta base. Si una tabla
              importada apunta a un registro de una tabla excluida que no existe aquí (p. ej. un usuario), esa
              referencia queda vacía cuando la columna lo permite; si no, la importación se detiene indicando la
              fila.
            </p>
          </div>
        ) : null}
        {!stats?.tables?.length ? (
          <p style={{ margin: 0, fontSize: '13px', color: ui.muted }}>
            Selecciona un dump JSON exportado desde esta plataforma para previsualizar las tablas.
          </p>
        ) : (
          <div style={{ overflowX: 'auto', maxHeight: 420 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 360 }}>
              <thead>
                <tr>
                  <th
                    style={{
                      width: 32,
                      padding: '6px 8px',
                      fontSize: '10px',
                      color: ui.muted,
                      borderBottom: `1px solid ${ui.border}`,
                    }}
                  >
                    Importar
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '6px 8px',
                      fontSize: '10px',
                      color: ui.muted,
                      borderBottom: `1px solid ${ui.border}`,
                    }}
                  >
                    Tabla
                  </th>
                  <th
                    style={{
                      textAlign: 'right',
                      padding: '6px 8px',
                      fontSize: '10px',
                      color: ui.muted,
                      borderBottom: `1px solid ${ui.border}`,
                    }}
                  >
                    Filas
                  </th>
                </tr>
              </thead>
              <tbody>
                {stats.tables.map((table) => (
                  <tr key={table.name} style={{ opacity: excludedSet.has(table.name) ? 0.5 : 1 }}>
                    <td style={{ padding: '6px 8px', borderBottom: `1px solid ${ui.border}`, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        aria-label={`Importar ${table.name}`}
                        checked={!excludedSet.has(table.name)}
                        disabled={busy}
                        onChange={() => toggleExcluded(table.name)}
                      />
                    </td>
                    <td
                      style={{
                        padding: '6px 8px',
                        fontSize: '12px',
                        color: ui.text,
                        borderBottom: `1px solid ${ui.border}`,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      }}
                    >
                      {table.name}
                    </td>
                    <td
                      style={{
                        padding: '6px 8px',
                        fontSize: '12px',
                        color: ui.text,
                        borderBottom: `1px solid ${ui.border}`,
                        textAlign: 'right',
                      }}
                    >
                      {table.rows.toLocaleString('es-CL')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemDataImportWorkspace;
